// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { NoteAddOutlined } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { NoteDialog } from '@/note/components/note-dialog';
import { NoteList } from '@/note/components/note-list';
import { createTicketNote } from '@/ticket/api/create-ticket-note';
import { getTicketNotes } from '@/ticket/api/get-ticket-notes';
import { useTicket } from '@/ticket/hooks/use-ticket';
import { Button } from '@mui/material';
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/tickets/$ticketKey/')({
  component: TicketNotesRoute,
});

function TicketNotesRoute() {
  const queryClient = useQueryClient();
  const { ticketKey } = Route.useParams();
  const { ticket } = useTicket(ticketKey);
  const noteDialog = useDisclosure();
  const { data } = useSuspenseQuery({
    queryKey: ['ticket', ticketKey, 'notes'],
    queryFn: () => getTicketNotes({ key: ticketKey }),
  });

  const addNoteMutation = useMutation({
    mutationFn: (note: string) => createTicketNote(ticket.key, { note: note }),
    onSuccess: () => {
      noteDialog.close();
      queryClient.invalidateQueries({
        queryKey: ['ticket', ticketKey, 'notes'],
      });
      queryClient.invalidateQueries({
        queryKey: ['ticket', ticketKey],
      });
    },
  });

  return (
    <HardfinTabCardWrapper
      title="Notes"
      actions={
        <Button
          size="small"
          onClick={() => noteDialog.open()}
          startIcon={<NoteAddOutlined fontSize="small" />}
        >
          Add note
        </Button>
      }
    >
      <NoteDialog
        isOpen={noteDialog.isOpen}
        onClose={noteDialog.close}
        onSubmit={(content) => addNoteMutation.mutate(content)}
        isSubmitting={addNoteMutation.isPending}
        title="Add ticket note"
        submitButtonText="Add"
      />
      <NoteList
        notes={data || []}
        onNotesUpdated={() => {
          queryClient.invalidateQueries({
            queryKey: ['ticket', ticketKey, 'notes'],
          });
        }}
      />
    </HardfinTabCardWrapper>
  );
}
